import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";

import { T } from "../constants";
import { cx, useUi } from "../ui";
import {
  clockLabel,
  dateLabel,
  dayChoices,
  dayLabel,
  dayOf,
  isoAt,
  timeSlots,
  whenText,
} from "../dayTime";
import MonthCalendar from "./MonthCalendar";

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", gap: 8 },
  chips: { display: "flex", gap: 6, flexWrap: "wrap" },
  chip: {
    border: `1px solid ${T.border}`,
    background: T.elevated,
    borderRadius: 999,
    padding: "7px 12px",
    fontSize: 13,
    cursor: "pointer",
    fontFamily: "inherit",
    color: T.fg,
    "&[aria-pressed='true']": {
      background: T.accent,
      color: T.accentFg,
      borderColor: T.primary,
      fontWeight: 600,
    },
    "&:focus-visible": { outline: `2px solid ${T.primary}`, outlineOffset: 2 },
  },
  panel: {
    border: `1px solid ${T.border}`,
    borderRadius: 8,
    padding: 12,
    display: "flex",
    flexDirection: "column",
    gap: 10,
    background: T.elevated,
  },
  head: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    fontSize: 13,
  },
  title: { fontWeight: 600, color: T.fg },
  back: {
    border: "none",
    background: "transparent",
    color: T.primary,
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: 13,
    padding: 0,
  },
  times: { display: "flex", gap: 6, flexWrap: "wrap" },
  time: {
    border: `1px solid ${T.border}`,
    background: T.elevated,
    borderRadius: 6,
    padding: "6px 10px",
    fontSize: 12,
    cursor: "pointer",
    fontFamily: "inherit",
    color: T.fg,
    fontVariantNumeric: "tabular-nums",
    "&[aria-pressed='true']": {
      background: T.accent,
      color: T.accentFg,
      borderColor: T.primary,
      fontWeight: 600,
    },
    "&:disabled": {
      color: T.subtleFg,
      background: T.surface,
      cursor: "default",
      textDecoration: "line-through",
    },
    "&:focus-visible": { outline: `2px solid ${T.primary}`, outlineOffset: 2 },
  },
  muted: { fontSize: 12, color: T.mutedFg },
  selected: { fontSize: 13, color: T.fg, fontWeight: 600 },
  manual: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" },
  manualInput: { width: 130 },
});

const DAY_CHOICES = 5;

const DEFAULT_STEP_MINUTES = 30;

const DEFAULT_HOURS = { from: 0, to: 23 };

export function DayChoices({
  tz,
  now,
  day,
  onSelect,
  from,
  label = "Day",
  count = DAY_CHOICES,
  calendarLabel = "Choose a date",
  allowPast = false,
}) {
  const s = useStyles();
  const [calendar, setCalendar] = useState(false);
  const choices = dayChoices({ from, count, tz });
  const custom = day && !choices.includes(day);

  return (
    <div className={s.root}>
      <div className={s.chips} role="group" aria-label={label}>
        {choices.map((item) => (
          <button
            key={item}
            type="button"
            className={s.chip}
            aria-pressed={!calendar && day === item}
            onClick={() => {
              setCalendar(false);
              onSelect(item);
            }}
          >
            {dayLabel({ day: item, tz, now })}
          </button>
        ))}
        <button
          type="button"
          className={s.chip}
          aria-pressed={calendar || Boolean(custom)}
          aria-expanded={calendar}
          onClick={() => setCalendar((current) => !current)}
        >
          {custom && !calendar ? dayLabel({ day, tz, now }) : "Calendar"}
        </button>
      </div>

      {calendar && (
        <div className={s.panel}>
          <div className={s.head}>
            <span className={s.title}>{calendarLabel}</span>
            <button type="button" className={s.back} onClick={() => setCalendar(false)}>
              Back
            </button>
          </div>
          <MonthCalendar
            tz={tz}
            now={now}
            selectedDay={day}
            onSelect={(picked) => {
              setCalendar(false);
              onSelect(picked);
            }}
            label={calendarLabel}
            allowPast={allowPast}
          />
        </div>
      )}
    </div>
  );
}

export function TimeChoices({
  tz,
  day,
  value,
  onSelect,
  label = "Time",
  stepMinutes = DEFAULT_STEP_MINUTES,
  hours = DEFAULT_HOURS,
  statusFor = () => null,
  manualLabel = "Enter a time",
}) {
  const s = useStyles();
  const ui = useUi();
  const [manual, setManual] = useState(false);
  const slots = timeSlots({ day, tz, stepMinutes, hours });
  const listed = slots.some((slot) => slot.iso === value);

  return (
    <div className={s.root}>
      <div className={s.times} role="group" aria-label={label}>
        {slots.map((slot) => {
          const status = statusFor(slot.iso);
          const disabled = Boolean(status?.disabled);

          return (
            <button
              key={slot.iso}
              type="button"
              className={s.time}
              aria-pressed={slot.iso === value}
              disabled={disabled}
              title={status?.reason ?? "Available"}
              aria-label={`${slot.label}${
                status?.reason ? ` · ${status.reason}` : ""
              }`}
              onClick={() => onSelect(slot.iso)}
            >
              {slot.label}
            </button>
          );
        })}
      </div>

      <div className={s.manual}>
        <button
          type="button"
          className={s.back}
          aria-expanded={manual}
          onClick={() => setManual((current) => !current)}
        >
          {manual ? "Hide manual time" : manualLabel}
        </button>
        {manual && (
          <input
            type="time"
            className={cx(ui.field, s.manualInput)}
            value={clockLabel(value, tz)}
            step={60}
            aria-label={`${label} (manual entry)`}
            onChange={(e) => {
              if (e.target.value) onSelect(isoAt({ day, clock: e.target.value, tz }));
            }}
          />
        )}
        {value && !listed && !manual && (
          <span className={s.muted}>Custom time · {clockLabel(value, tz)}</span>
        )}
      </div>
    </div>
  );
}

/**
 * The prompted day-and-time control every booking date uses.
 *
 * A day is a chip or the calendar; a time is a slot on that day. Anything at or
 * before `minIso` is offered struck through and unselectable rather than left
 * out, so the operator can see the shape of the day and read why a time is
 * closed to them. `allowPast` only opens earlier months in the calendar - an
 * existing booking may well sit behind us - and never lifts that floor.
 */
export default function DayTimePicker({
  tz,
  now,
  value,
  onChange,
  label = "Date and time",
  minIso = null,
  stepMinutes = DEFAULT_STEP_MINUTES,
  hours = DEFAULT_HOURS,
  allowPast = false,
  dayCount = DAY_CHOICES,
  minReason = "Earlier than the time already chosen",
}) {
  const s = useStyles();

  const from = (minIso ? dayOf(minIso, tz) : null) ?? dayOf(now, tz);

  const [day, setDay] = useState(() => dayOf(value, tz) ?? from);

  /* The floor moved past the day on screen - a later arrival, or a modal left
     open into the next day. Follow it rather than showing a day of struck-out
     times. */
  useEffect(() => {
    if (!from || day >= from) return;

    setDay(from);
  }, [from, day]);

  /* Compared as instants, never as strings: two ISO stamps either side of a
     daylight-saving change carry different offsets. */
  const floor = minIso ? Date.parse(minIso) : null;

  const statusFor = (iso) => {
    if (floor === null || Date.parse(iso) > floor) return null;

    return { disabled: true, reason: minReason };
  };

  return (
    <div className={s.root} aria-label={label}>
      <DayChoices
        tz={tz}
        now={now}
        day={day}
        onSelect={setDay}
        from={from}
        label={`${label} · day`}
        count={dayCount}
        allowPast={allowPast}
      />

      <div className={s.panel}>
        <div className={s.head}>
          <span className={s.title}>
            {dayLabel({ day, tz, now })} · {dateLabel({ day, tz })}
          </span>
        </div>
        <TimeChoices
          tz={tz}
          day={day}
          value={value}
          onSelect={onChange}
          label={`${label} · time`}
          stepMinutes={stepMinutes}
          hours={hours}
          statusFor={statusFor}
        />
        <div className={s.muted}>Unavailable times cannot be chosen.</div>
      </div>

      {value && (
        <div className={s.selected} role="status">
          Selected · {whenText({ iso: value, tz, now })}
        </div>
      )}
    </div>
  );
}
